import { Group, Tabs } from '@mantine/core'
import {
  IconAlertTriangleFilled,
  IconCreditCard,
  IconReceipt,
  IconTag,
  IconUsers,
  IconWallet,
} from '@tabler/icons-react'
import { parse } from 'qs'
import { useState } from 'react'
import { useSearch } from 'wouter'

import { Screen } from '@/components/Screen'
import { useBillingDisabledStatus } from '@/hooks/useBillingDisabledStatus'

import { BillingTab } from './BillingTab'
import { PaymentTab } from './PaymentTab'
import { PricingTab } from './PricingTab'
import { SubscribersTab } from './SubscribersTab'

const VALID_TABS = ['pricing', 'billing', 'payment', 'subscribers'] as const
type BillingTabType = (typeof VALID_TABS)[number]

export function BillingScreen() {
  const searchParamsString = useSearch()
  const searchParams: { tab?: string } = parse(searchParamsString)
  const { ignoreBilling, isBillingRestricted } = useBillingDisabledStatus()
  const [activeTab, setActiveTab] = useState<BillingTabType>('pricing')
  const [syncedFrom, setSyncedFrom] = useState<{ tab?: string; ignoreBilling: boolean } | null>(null)

  if (!syncedFrom || syncedFrom.tab !== searchParams.tab || syncedFrom.ignoreBilling !== ignoreBilling) {
    setSyncedFrom({ tab: searchParams.tab, ignoreBilling })
    if (ignoreBilling) {
      setActiveTab('subscribers')
    } else if (searchParams.tab && VALID_TABS.includes(searchParams.tab as BillingTabType)) {
      setActiveTab(searchParams.tab as BillingTabType)
    }
  }

  if (ignoreBilling) {
    return (
      <Screen title="Account Management" icon={IconCreditCard}>
        <Tabs value="subscribers" onChange={() => {}}>
          <Tabs.List>
            <Tabs.Tab value="subscribers" leftSection={<IconUsers size={16} />}>
              Users
            </Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="subscribers" pt="xl">
            <SubscribersTab />
          </Tabs.Panel>
        </Tabs>
      </Screen>
    )
  }

  return (
    <Screen title="Account Management" icon={IconCreditCard}>
      <Tabs value={activeTab} onChange={value => setActiveTab(value as BillingTabType)}>
        <Tabs.List>
          <Tabs.Tab value="pricing" leftSection={<IconTag size={16} />}>
            Pricing
          </Tabs.Tab>
          <Tabs.Tab value="billing" leftSection={<IconReceipt size={16} />}>
            Billing
          </Tabs.Tab>
          <Tabs.Tab
            value="payment"
            leftSection={<IconWallet size={16} />}
            color={isBillingRestricted ? 'orange' : undefined}>
            <Group gap={6}>
              Payment
              {isBillingRestricted && <IconAlertTriangleFilled size={14} color="var(--mantine-color-orange-6)" />}
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="subscribers" leftSection={<IconUsers size={16} />}>
            Users
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="pricing" pt="xl">
          <PricingTab />
        </Tabs.Panel>

        <Tabs.Panel value="billing" pt="xl">
          <BillingTab />
        </Tabs.Panel>

        <Tabs.Panel value="payment" pt="xl">
          <PaymentTab />
        </Tabs.Panel>

        <Tabs.Panel value="subscribers" pt="xl">
          <SubscribersTab />
        </Tabs.Panel>
      </Tabs>
    </Screen>
  )
}
